import { ACCOUNTING_CA } from './accounting-ca.js';
import { ON_LAW_BYLAW9 } from './on-law-bylaw9.js';
import { ON_LAW_IMMIGRATION } from './on-law-immigration.js';
import type { IPack, IPackDocType, IPackField, IRecordCategory, PackId } from './types.js';

export type { IPack, IPackDeadline, IPackDocType, IPackField, IRecordCategory, PackId } from './types.js';
export { addDays, deadlinesFor, deadlineWords, type IDeadlineEstimate } from './deadlines.js';
export { ACCOUNTING_CA, ACCOUNTING_CATEGORIES } from './accounting-ca.js';
export { ON_LAW_BYLAW9 } from './on-law-bylaw9.js';
export { ON_LAW_IMMIGRATION } from './on-law-immigration.js';
export { type IRetentionLabelBody, type IRetentionLabelPlan, LABEL_NAME_MAX, retentionLabelBodies, retentionLabelName } from './retentionLabels.js';

export const PACKS: { [id in PackId]: IPack } = {
  'accounting-ca': ACCOUNTING_CA,
  'on-law-bylaw9': ON_LAW_BYLAW9,
  'on-law-immigration': ON_LAW_IMMIGRATION
};

export const DEFAULT_PACK: IPack = ACCOUNTING_CA;

export function isPackId(v: unknown): v is PackId {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(PACKS, v);
}

/** The pack by id; anything unknown is the accounting pack (what every library used before packs). */
export function getPack(id: string | undefined): IPack {
  return isPackId(id) ? PACKS[id] : DEFAULT_PACK;
}

/** Ontario law packs (By-Law 9 and its variants): matters, the retention schedule and the spot audit. */
export function isLawPack(pack: IPack): boolean {
  return pack.regulator === 'Law Society of Ontario';
}

export function docTypeOf(pack: IPack, id: string | undefined): IPackDocType | undefined {
  return pack.docTypes.filter(t => t.id === id)[0];
}

/** The extra facts read from documents of this type (none for most types). */
export function fieldsOf(pack: IPack, docTypeId: string | undefined): IPackField[] {
  const ids: string[] = docTypeOf(pack, docTypeId)?.fields ?? [];
  return (pack.fields ?? []).filter(f => ids.indexOf(f.id) !== -1);
}

/** How long a document of this type is kept, and why (regulated packs only). */
export function retentionOf(pack: IPack, docTypeId: string | undefined): IRecordCategory | undefined {
  const type: IPackDocType | undefined = docTypeOf(pack, docTypeId);
  return type?.recordCategory ? pack.recordCategories.filter(c => c.id === type.recordCategory)[0] : undefined;
}

/** Every category name any pack uses (for checking saved folder layouts). */
export function allCategories(): string[] {
  const out: string[] = [];
  Object.keys(PACKS).forEach(id => PACKS[id as PackId].categories.forEach(c => { if (out.indexOf(c) === -1) { out.push(c); } }));
  return out;
}
